import { useState } from 'react';
import {
  Award,
  Building2,
  Hammer,
  Landmark,
  Medal,
  Shield,
  Swords,
  UsersRound,
} from 'lucide-react';
import { DEFAULT_PREFERENCES } from '../data/demoCitizen';
import {
  formatCitizenSince,
  formatCompactNumber,
  formatUpdatedAt,
  serialForCitizen,
} from '../lib/format';
import type { CitizenCardData, CitizenCardPreferences } from '../types/citizen';
import { CitizenAvatar } from './CitizenAvatar';
import { CountryFlag } from './CountryFlag';
import { EclesiarMark } from './EclesiarMark';

type CardTab = 'identity' | 'service';

interface CitizenCardProps {
  data: CitizenCardData;
  preferences?: CitizenCardPreferences;
  initialTab?: CardTab;
  className?: string;
  previewLabel?: string;
}

interface RecordRowProps {
  icon: typeof Shield;
  label: string;
  value: string;
  meta?: string;
  imageUrl?: string | null;
  imageVariant?: 'affiliation' | 'rank';
}

interface RecordVisualProps {
  icon: typeof Shield;
  imageUrl?: string | null;
  imageVariant?: 'affiliation' | 'rank';
}

function RecordVisual({ icon: Icon, imageUrl, imageVariant }: RecordVisualProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl && !imageFailed);

  return (
    <span
      className={`record-row__icon${imageVariant ? ` record-row__icon--${imageVariant}` : ''}`}
      aria-hidden="true"
    >
      <Icon size={16} strokeWidth={1.8} />
      {showImage ? (
        <img src={imageUrl ?? undefined} alt="" onError={() => setImageFailed(true)} />
      ) : null}
    </span>
  );
}

function RecordRow({ icon, label, value, meta, imageUrl, imageVariant }: RecordRowProps) {
  return (
    <div className={`record-row${imageVariant === 'rank' ? ' record-row--rank' : ''}`}>
      <RecordVisual
        key={imageUrl ?? 'fallback'}
        icon={icon}
        imageUrl={imageUrl}
        imageVariant={imageVariant}
      />
      <span className="record-row__copy">
        <span className="record-row__label">{label}</span>
        <span className="record-row__value">{value}</span>
      </span>
      {meta ? <span className="record-row__meta">{meta}</span> : null}
    </div>
  );
}

export function CitizenCard({
  data,
  preferences = DEFAULT_PREFERENCES,
  initialTab = 'identity',
  className = '',
  previewLabel,
}: CitizenCardProps) {
  const [tab, setTab] = useState<CardTab>(initialTab);
  const { citizen, service, affiliations } = data;
  const militaryRank = preferences.showMilitaryRank ? service.militaryRank : null;
  const builderRank = preferences.showBuilderRank ? service.builderRank : null;
  const totalDamage = preferences.showTotalDamage ? service.totalDamage : null;
  const buildingProgress = preferences.showBuildingProgress ? service.buildingProgress : null;
  const hasServiceRecord = Boolean(
    militaryRank || builderRank || totalDamage !== null || buildingProgress !== null,
  );

  return (
    <article className={`citizen-card ${className}`} aria-label={`${citizen.username}'s Eclesiar Citizen ID`}>
      <div className="citizen-card__security-line" aria-hidden="true" />
      <header className="citizen-card__header">
        <div className="citizen-card__brand">
          <span className="citizen-card__mark"><EclesiarMark /></span>
          <span>
            <strong>ECLESIAR</strong>
            <small>Citizen identity registry</small>
          </span>
        </div>
        <div className="citizen-card__state">
          <span className={`status-dot ${data.registryStatus === 'active' ? 'status-dot--active' : ''}`} />
          {data.registryStatus === 'active' ? 'Active' : 'Recorded'}
        </div>
      </header>

      <section className="citizen-card__identity">
        <CitizenAvatar citizen={citizen} />
        <div className="identity-copy">
          <span className="identity-copy__serial">{serialForCitizen(citizen.id)}</span>
          <h1>{citizen.username}</h1>
          <div className="identity-copy__country">
            <CountryFlag country={citizen.country} />
            <span>Citizen of {citizen.country.name}</span>
          </div>
          <div className="identity-copy__meta">
            <span><small>Level</small>{citizen.level}</span>
            <span><small>Since</small>{formatCitizenSince(citizen.citizenSince)}</span>
          </div>
        </div>
      </section>

      <div className="citizen-card__tabs" role="tablist" aria-label="Citizen record sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'identity'}
          className={tab === 'identity' ? 'is-active' : ''}
          onClick={() => setTab('identity')}
        >
          Identity
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'service'}
          className={tab === 'service' ? 'is-active' : ''}
          onClick={() => setTab('service')}
        >
          Service record
        </button>
      </div>

      <div className="citizen-card__record" role="tabpanel" key={tab}>
        {tab === 'identity' ? (
          <div className="record-stack">
            {preferences.showAffiliations ? (
              <>
                <RecordRow
                  icon={UsersRound}
                  label="Military unit"
                  value={affiliations.militaryUnit?.name ?? 'Independent citizen'}
                  imageUrl={affiliations.militaryUnit?.avatarUrl}
                  imageVariant="affiliation"
                />
                <RecordRow
                  icon={Landmark}
                  label="Political party"
                  value={affiliations.politicalParty?.name ?? 'No party declared'}
                  imageUrl={affiliations.politicalParty?.avatarUrl}
                  imageVariant="affiliation"
                />
              </>
            ) : (
              <RecordRow icon={Shield} label="Registry status" value="Identity verified" />
            )}
            <div className="civic-oath">
              <EclesiarMark />
              <p><span>Public record</span>Standing with {citizen.country.name}</p>
              <CountryFlag country={citizen.country} />
            </div>
          </div>
        ) : (
          <div className="service-record">
            <div className="record-stack record-stack--ranks">
              {militaryRank ? (
                <RecordRow
                  icon={Swords}
                  label="Military commission"
                  value={militaryRank.name}
                  meta={`R${militaryRank.level}`}
                  imageUrl={militaryRank.iconUrl}
                  imageVariant="rank"
                />
              ) : null}
              {builderRank ? (
                <RecordRow
                  icon={Hammer}
                  label="Civic commission"
                  value={builderRank.name}
                  meta={`R${builderRank.level}`}
                  imageUrl={builderRank.iconUrl}
                  imageVariant="rank"
                />
              ) : null}
            </div>
            <div className="service-stats">
              {totalDamage !== null ? (
                <div><Swords size={14} /><strong>{formatCompactNumber(totalDamage)}</strong><span>Total damage</span></div>
              ) : null}
              {buildingProgress !== null ? (
                <div><Building2 size={14} /><strong>{formatCompactNumber(buildingProgress)}</strong><span>Built for nation</span></div>
              ) : null}
            </div>
            {hasServiceRecord ? (
              <div className="commendations" aria-label="Highlighted commendations">
                <span><Award size={13} /> Commendations</span>
                <div>
                  {service.achievements.slice(0, 3).map((achievement, index) => (
                    <span key={achievement.id} title={achievement.name} className={`medal medal--${index + 1}`}>
                      <Medal size={14} />
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="civic-oath">
                <Shield />
                <p><span>Private record</span>Optional service fields are hidden</p>
              </div>
            )}
          </div>
        )}
      </div>

      <footer className="citizen-card__footer">
        <span>Record updated · {formatUpdatedAt(data.updatedAt)}</span>
        <span className="citizen-card__verified"><Shield size={12} fill="currentColor" /> Verified · eclesiar.com</span>
      </footer>
      {previewLabel ? <span className="citizen-card__preview-label">{previewLabel}</span> : null}
    </article>
  );
}
